import { resolveExpiredSessionLogin } from "./access-control"
import type { NamedAnswerReport } from "./named-answer-report"

// API client configuration for connecting to PostulaT backend
import type {
  Organization,
  CreateOrganizationInput,
  UpdateOrganizationInput,
  User,
  CreateUserInput,
  UpdateUserInput,
  UserFilters,
  Candidate,
  UpdateCandidateInput,
  CandidateRating,
  CreateRatingInput,
  UpdateRatingInput,
  RatingFilters,
  Vacancy,
  UpdateVacancyInput,
  VacancyFilters,
  VacancyTemplate,
  CreateTemplateInput,
  UpdateTemplateInput,
  ProcessTemplate,
  CreateProcessTemplateInput,
  UpdateProcessTemplateInput,
  Application,
  UpdateApplicationInput,
  ApplicationFilters,
  ApplicationHistoryEntry,
  CandidateHistoryEntry,
  CandidateHistoryFilters,
  CandidateFieldQualification,
  QualificationFilters,
  QualificationStatus,
  VacancyPipelineData,
  VacancyPipelineFilters,
  VacancyPipelineSection,
  Service,
  CreateServiceInput,
  UpdateServiceInput,
  Order,
  OrderFilters,
  Course,
  CreateCourseInput,
  UpdateCourseInput,
  Notification,
  CreateNotificationInput,
  NotificationFilters,
  SystemMetrics,
  RevenueReport,
  UserActivityReport,
  PaginationParams,
  Catalog,
  CreateCatalogInput,
  UpdateCatalogInput,
  CatalogFilters,
  SystemLog,
  SystemLogFilters,
  AssessmentBulkParticipantRow,
  AssessmentBulkParticipantsResponse,
  QuestionnaireTypeIndex,
} from "./types"
import type { ProcessStageStatus } from "./database.enums"
import type { RoleApplication, RoleCatalogEntry, RoleDraft, RolePermission } from "./role-editor"
import type {
  AccessAssignment,
  AccessAssignmentInput,
  AccessAssignmentSyncInput,
} from "./access-control"

// Use Next.js API routes
const API_BASE_URL = "/api"

export class ApiClientError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly details?: unknown,
  ) {
    super(message)
    this.name = "ApiClientError"
  }
}

export class ApiClient {
  async getNomCatalog(manage = false) { return this.request<any>(`questionnaire/admin/nom-catalog?manage=${manage}`) }
  async saveNomCatalog(body: Record<string, unknown>) { return this.request<any>('questionnaire/admin/nom-catalog', { method: 'POST', body: JSON.stringify(body) }) }
  async getSurveyNom(id: string) { return this.request<any>(`questionnaire/admin/questionnaires/${id}/nom`) }
  async attachNom(id: string, version_id: string | string[] | null) { return this.request<any>(`questionnaire/admin/questionnaires/${id}/nom`, { method: 'PUT', body: JSON.stringify(Array.isArray(version_id) ? { version_ids: version_id } : { version_id }) }) }
  async getNomReport(id: string, mode: string) { return this.request<any>(`questionnaire/admin/reports/${id}/nom?mode=${mode}`) }
  async getNomAnswerReport(id: string, mode: string, versionId?: string, evaluationId?: string) {
    const query = new URLSearchParams({ mode, format: "answers" })
    if (versionId) query.set("nom_version_id", versionId)
    if (evaluationId) query.set("evaluation_id", evaluationId)
    return this.request<{ answer_report: NamedAnswerReport }>(`questionnaire/admin/reports/${id}/nom?${query}`)
  }

  private baseUrl: string
  private activeOrganizationId: string | null = null

  setActiveOrganizationId(organizationId: string | null): void {
    this.activeOrganizationId = organizationId
  }

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl
  }

  private buildQueryString(params?: Record<string, any>): string {
    if (!params) return ""
    const filtered = Object.entries(params)
      .filter(([_, value]) => value !== undefined && value !== null)
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {})
    const query = new URLSearchParams(filtered as any).toString()
    return query ? `?${query}` : ""
  }

  private async request<T>(endpoint: string, options: RequestInit = {}, retryOn401: boolean = true, isPublicEndpoint: boolean = false): Promise<T> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    }

    const requestHeaders = new Headers(headers)
    if (this.activeOrganizationId && (endpoint.startsWith("questionnaire/") || endpoint.startsWith("postulate/admin/"))) {
      requestHeaders.set("X-Organization-Id", this.activeOrganizationId)
    }
    new Headers(options.headers).forEach((value, key) => requestHeaders.set(key, value))

    const response = await fetch(`${this.baseUrl}/${endpoint}`, {
      ...options,
      headers: requestHeaders,
    })

    if (!response.ok) {
      // Handle 401 Unauthorized - try to refresh token (only for authenticated endpoints)
      if (response.status === 401 && retryOn401 && !isPublicEndpoint) {
        try {
          // Attempt to refresh the token
          const refreshResponse = await fetch("/api/auth/refresh", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
          })

          if (refreshResponse.ok) {
            // Token refreshed successfully, retry the original request
            console.log("[ApiClient] Token refreshed, retrying request...")
            return this.request<T>(endpoint, options, false, isPublicEndpoint) // Don't retry again if this fails
          } else {
            // Refresh failed, redirect to login
            console.error("[ApiClient] Token refresh failed, redirecting to login")
            if (typeof window !== 'undefined') {
              window.location.href = resolveExpiredSessionLogin(window.location.pathname, window.location.search)
            }
            throw new Error("Tu sesión ha expirado. Por favor, inicia sesión nuevamente.")
          }
        } catch (refreshError) {
          // Refresh request itself failed, redirect to login
          console.error("[ApiClient] Token refresh error:", refreshError)
          if (typeof window !== 'undefined') {
            window.location.href = resolveExpiredSessionLogin(window.location.pathname, window.location.search)
          }
          throw new Error("Tu sesión ha expirado. Por favor, inicia sesión nuevamente.")
        }
      } else if (response.status === 401 && !isPublicEndpoint) {
        // Already tried refresh or retryOn401 is false, redirect to login (only for authenticated endpoints)
        if (typeof window !== 'undefined') {
          window.location.href = resolveExpiredSessionLogin(window.location.pathname, window.location.search)
        }
        throw new Error("Tu sesión ha expirado. Por favor, inicia sesión nuevamente.")
      }

      const errorData = await response.json().catch(() => ({}))
      // Backend wraps payload in { body: { message, ... }, message: statusText }
      const errorMessage =
        errorData.body?.message ||
        (typeof errorData.message === "string" &&
        errorData.message !== response.statusText &&
        errorData.message !== "Bad Request" &&
        errorData.message !== "Not Found" &&
        errorData.message !== "Forbidden"
          ? errorData.message
          : null) ||
        errorData.error ||
        `API Error: ${response.statusText}`
      console.error(`[ApiClient] Error ${response.status} on ${endpoint}:`, {
        error: errorData,
        status: response.status,
        statusText: response.statusText,
        endpoint: endpoint
      })
      // Explicit field logging so details are visible without expanding objects
      if (errorData?.body) {
        console.error(`[ApiClient] Error body:`, JSON.stringify(errorData.body))
      }
      throw new ApiClientError(errorMessage, response.status, errorData)
    }

    const data = await response.json()
    
    // Backend returns data wrapped in { id, body, status, message }
    // Unwrap the body property if it exists
    if (data.body !== undefined) {
      return data.body as T
    }
    
    return data as T
  }

  // Organizations
  async getOrganizations() {
    return this.request<{ organizations: Organization[] }>("postulate/admin/organizations")
  }

  async getOrganization(id: string) {
    return this.request<{ organization: Organization; users: any[]; vacancies_count: number }>(
      `postulate/admin/organizations/${id}`
    )
  }

  async createOrganization(data: CreateOrganizationInput) {
    return this.request<{ organization: Organization }>("postulate/admin/organizations", {
      method: "POST",
      body: JSON.stringify(data),
    })
  }

  async updateOrganization(id: string, data: UpdateOrganizationInput) {
    return this.request<{ organization: Organization }>(`postulate/admin/organizations/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    })
  }

  async deleteOrganization(id: string) {
    return this.request<{ organization: Organization }>(`postulate/admin/organizations/${id}`, {
      method: "DELETE",
    })
  }

  async addUserToOrganization(organizationId: string, userId: string, orgRole: string = "member") {
    return this.request<{ organization_user: any }>(`postulate/admin/organizations/${organizationId}/add-user`, {
      method: "POST",
      body: JSON.stringify({
        organization_id: organizationId,
        user_id: userId,
        org_role: orgRole,
      }),
    })
  }

  async removeUserFromOrganization(organizationId: string, userId: string) {
    return this.request<{ message: string }>(`postulate/admin/organizations/${organizationId}/remove-user`, {
      method: "DELETE",
      body: JSON.stringify({
        organization_id: organizationId,
        user_id: userId,
      }),
    })
  }

  async updateUserOrganizationRole(
    organizationId: string,
    userId: string,
    orgRole?: string,
    isActive?: boolean
  ) {
    return this.request<{ organization_user: any }>(`postulate/admin/organizations/${organizationId}/update-user-role`, {
      method: "PUT",
      body: JSON.stringify({
        organization_id: organizationId,
        user_id: userId,
        org_role: orgRole,
        is_active: isActive,
      }),
    })
  }

  // Users
  async getUsers(params?: UserFilters) {
    const query = this.buildQueryString(params)
    return this.request<{
      users: User[]
      pagination: { page: number; limit: number; total: number; total_pages: number }
    }>(`postulate/admin/users${query}`)
  }

  async getUser(id: string) {
    return this.request<{ user: User; candidate_profile: any; organizations: any[] }>(
      `postulate/admin/users/${id}`
    )
  }

  async createUser(data: CreateUserInput) {
    return this.request<{ user: User }>("postulate/admin/users", {
      method: "POST",
      body: JSON.stringify(data),
    })
  }

  async updateUser(id: string, data: UpdateUserInput) {
    return this.request<{ user: User }>(`postulate/admin/users/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    })
  }

  async deleteUser(id: string) {
    return this.request<{ message: string }>(`postulate/admin/users/${id}`, {
      method: "DELETE",
    })
  }

  async sendUserPasswordReset(id: string) {
    return this.request<{ email_sent: boolean }>(`postulate/admin/users/${id}/reset-password`, {
      method: "POST",
    }, false)
  }

  // Candidates
  async getCandidates(params?: PaginationParams) {
    const query = this.buildQueryString(params)
    return this.request<{
      candidates: Candidate[]
      pagination: { page: number; limit: number; total: number; total_pages: number }
    }>(`postulate/admin/candidates${query}`)
  }

  async getCandidate(id: string) {
    return this.request<{
      candidate: Candidate
      skills: any[]
      documents: any[]
      applications: Application[]
    }>(`postulate/admin/candidates/${id}`)
  }

  async updateCandidate(id: string, data: UpdateCandidateInput) {
    return this.request<{ candidate: Candidate }>(`postulate/admin/candidates/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    })
  }

  async createCandidate(data: { user_id: string; [key: string]: any }) {
    return this.request<{ candidate: Candidate }>("postulate/admin/candidates", {
      method: "POST",
      body: JSON.stringify(data),
    })
  }

  async deleteCandidate(id: string) {
    return this.request<{ message: string }>(`postulate/admin/candidates/${id}`, {
      method: "DELETE",
    })
  }

  // ============================================
  // CANDIDATE RATINGS
  // ============================================
  
  /**
   * Get all ratings with optional filters
   * @param params - Filter parameters (candidate_id, rater_id, organization_id, rater_type, application_id, min_hard_skills, etc.)
   * @returns Ratings list with pagination
   */
  async getRatings(params?: RatingFilters) {
    const query = this.buildQueryString(params)
    return this.request<{
      ratings: CandidateRating[]
      pagination: { page: number; limit: number; total: number; total_pages: number }
    }>(`postulate/admin/ratings${query}`)
  }

  /**
   * Get a single rating by ID
   * @param id - Rating ID
   * @returns Rating details
   */
  async getRating(id: string) {
    return this.request<{ rating: CandidateRating }>(`postulate/admin/ratings/${id}`)
  }

  /**
   * Create a new rating for a candidate
   * @param data - Rating data including candidate_id, rating, dimension_ratings (per-skill), application_id (optional), comments
   * @returns Created rating
   */
  async createRating(data: CreateRatingInput) {
    // Validate required fields
    if (!data.candidate_id) {
      throw new Error("candidate_id is required")
    }
    if (data.rating === undefined || data.rating === null) {
      throw new Error("rating is required")
    }
    
    // Ensure rating is a number
    const ratingData = {
      ...data,
      rating: Number(data.rating)
    }
    
    console.log("[ApiClient] Creating rating:", JSON.stringify(ratingData, null, 2))
    
    return this.request<{ rating: CandidateRating }>("postulate/admin/ratings", {
      method: "POST",
      body: JSON.stringify(ratingData),
    })
  }

  /**
   * Update an existing rating
   * @param id - Rating ID
   * @param data - Updated rating data (rating, dimension_ratings, comments, application_id)
   * @returns Updated rating
   */
  async updateRating(id: string, data: UpdateRatingInput) {
    return this.request<{ rating: CandidateRating }>(`postulate/admin/ratings/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    })
  }

  /**
   * Delete a rating
   * @param id - Rating ID
   * @returns Success message
   */
  async deleteRating(id: string) {
    return this.request<{ message: string }>(`postulate/admin/ratings/${id}`, {
      method: "DELETE",
    })
  }

  /**
   * Get ratings for a specific candidate
   * @param candidateId - Candidate ID
   * @param applicationId - Optional application ID to filter application-specific ratings
   * @returns Ratings for the candidate
   */
  async getCandidateRatings(candidateId: string, applicationId?: string) {
    const params: RatingFilters = { candidate_id: candidateId }
    if (applicationId) {
      params.application_id = applicationId
    }
    return this.getRatings(params)
  }

  /**
   * Get ratings for a specific application
   * @param applicationId - Application ID
   * @returns Ratings for the application
   */
  async getApplicationRatings(applicationId: string) {
    return this.getRatings({ application_id: applicationId })
  }

  // ============================================
  // QUALIFICATIONS
  // ============================================

  /**
   * Get pending qualifications for candidates
   * @param params - Filter parameters (candidate_id, page, limit)
   * @returns Pending qualifications list with pagination
   */
  async getPendingQualifications(params?: QualificationFilters) {
    const query = this.buildQueryString(params)
    return this.request<{
      qualifications: CandidateFieldQualification[]
      pagination: { page: number; limit: number; total: number; total_pages: number }
    }>(`postulate/admin/qualifications/pending${query}`)
  }

  /**
   * Get qualification history for a candidate field
   * @param params - Filter parameters (candidate_id, field_name, page, limit)
   * @returns Qualification history with pagination
   */
  async getQualificationHistory(params: QualificationFilters & { candidate_id: string }) {
    const query = this.buildQueryString(params)
    return this.request<{
      qualifications: CandidateFieldQualification[]
      pagination: { page: number; limit: number; total: number; total_pages: number }
    }>(`postulate/admin/qualifications/history${query}`)
  }

  /**
   * Manually mark a field as qualified
   * @param data - Qualification data (candidate_id, field_name, rating_id optional)
   * @returns Updated qualification
   */
  async markFieldQualified(data: { candidate_id: string; field_name: string; rating_id?: string }) {
    return this.request<{ qualification: CandidateFieldQualification }>("postulate/admin/qualifications/mark-qualified", {
      method: "POST",
      body: JSON.stringify(data),
    })
  }

  // ============================================
  // CATALOG TYPES
  // ============================================

  /**
   * Get all catalog types with metadata
   */
  async getCatalogTypes() {
    return this.request<{
      catalog_types: Array<{
        catalog_type: string
        count: number
        active_count: number
        requires_qualification: boolean
      }>
    }>("postulate/admin/catalog-types")
  }

  /**
   * Get qualifiable catalog types (types that require qualification)
   */
  async getQualifiableCatalogTypes() {
    return this.request<{
      qualifiable_types: string[]
      count: number
      field_to_catalog_type: Record<string, string>
      catalog_type_to_field: Record<string, string>
    }>("postulate/admin/catalog-types/qualifiable")
  }

  /**
   * Create a new catalog type
   */
  async createCatalogType(data: { catalog_type: string; description?: string }) {
    return this.request<{
      catalog_type: string
      placeholder_catalog: Catalog
    }>("postulate/admin/catalog-types", {
      method: "POST",
      body: JSON.stringify(data),
    })
  }

  /**
   * Update a catalog type (renames all catalogs of that type)
   */
  async updateCatalogType(oldType: string, data: { catalog_type: string }) {
    return this.request<{
      catalog_type: string
      updated_count: number
    }>(`postulate/admin/catalog-types?old_type=${encodeURIComponent(oldType)}`, {
      method: "PUT",
      body: JSON.stringify(data),
    })
  }

  /**
   * Delete a catalog type (deletes all catalogs of that type)
   */
  async deleteCatalogType(type: string) {
    return this.request<{
      message: string
      deleted_count: number
    }>(`postulate/admin/catalog-types?type=${encodeURIComponent(type)}`, {
      method: "DELETE",
    })
  }

  /**
   * Bulk update a catalog type (updates all catalogs of that type)
   */
  async bulkUpdateCatalogType(type: string, data: { requires_qualification?: boolean; is_active?: boolean; is_universal?: boolean; applies_to_entities?: string[] }) {
    return this.request<{
      catalog_type: string
      updated_count: number
      updates: any
      // NOTE: backend PATCHes /catalog-types (the old Next proxy mapped the
      // /bulk-update path segment to this query-param form).
    }>(`postulate/admin/catalog-types?type=${encodeURIComponent(type)}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    })
  }

  // Vacancies
  async getVacancies(params?: VacancyFilters) {
    const query = this.buildQueryString(params)
    return this.request<{
      vacancies: Vacancy[]
      pagination: { page: number; limit: number; total: number; total_pages: number }
    }>(`postulate/admin/vacancies${query}`)
  }

  async getVacancy(id: string) {
    return this.request<{ vacancy: Vacancy; skills: any[]; applications_count: number }>(
      `postulate/admin/vacancies/${id}`
    )
  }

  async createVacancy(data: { organization_id: string; title: string; [key: string]: any }) {
    return this.request<{ vacancy: Vacancy }>("postulate/admin/vacancies", {
      method: "POST",
      body: JSON.stringify(data),
    })
  }

  async updateVacancy(id: string, data: UpdateVacancyInput) {
    return this.request<{ vacancy: Vacancy }>(`postulate/admin/vacancies/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    })
  }

  async deleteVacancy(id: string) {
    return this.request<{ vacancy: Vacancy }>(`postulate/admin/vacancies/${id}`, {
      method: "DELETE",
    })
  }

  async exportVacancyToExcel(id: string): Promise<Blob> {
    const response = await fetch(`${this.baseUrl}/postulate/admin/vacancies/${id}/export-excel`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    })

    if (!response.ok) {
      // Handle 401 Unauthorized - session expired
      if (response.status === 401) {
        if (typeof window !== 'undefined') {
          window.location.href = resolveExpiredSessionLogin(window.location.pathname, window.location.search)
        }
        throw new Error("Tu sesión ha expirado. Por favor, inicia sesión nuevamente.")
      }

      const errorData = await response.json().catch(() => ({}))
      const errorMessage = errorData.message || errorData.body?.message || errorData.error || `API Error: ${response.statusText}`
      console.error(`[ApiClient] Error ${response.status} on exportVacancyToExcel:`, errorData)
      throw new Error(errorMessage)
    }

    return response.blob()
  }

  async exportAllVacanciesToExcel(): Promise<Blob> {
    const response = await fetch(`${this.baseUrl}/postulate/admin/vacancies/export-excel`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    })

    if (!response.ok) {
      // Handle 401 Unauthorized - session expired
      if (response.status === 401) {
        if (typeof window !== 'undefined') {
          window.location.href = resolveExpiredSessionLogin(window.location.pathname, window.location.search)
        }
        throw new Error("Tu sesión ha expirado. Por favor, inicia sesión nuevamente.")
      }

      const errorData = await response.json().catch(() => ({}))
      const errorMessage = errorData.message || errorData.body?.message || errorData.error || `API Error: ${response.statusText}`
      console.error(`[ApiClient] Error ${response.status} on exportAllVacanciesToExcel:`, errorData)
      throw new Error(errorMessage)
    }

    return response.blob()
  }

  // Vacancy Processes
  async getVacancyProcesses(id: string) {
    return this.request<{
      selection_process: Array<{
        code: string
        order: number
        is_required: boolean
        responsible_user_id?: string | null
        responsible_user?: {
          id: string
          email: string
          full_name: string | null
          role: string
        } | null
      }>
      hiring_process: Array<{
        code: string
        order: number
        is_required: boolean
        responsible_user_id?: string | null
        responsible_user?: {
          id: string
          email: string
          full_name: string | null
          role: string
        } | null
      }>
      process_flow?: Array<{
        code: string
        process_type: 'selection' | 'hiring'
        order: number
        display_order: number
        is_required: boolean
        responsible_user_id?: string | null
        responsible_user?: {
          id: string
          email: string
          full_name: string | null
          role: string
        } | null
      }>
    }>(`postulate/admin/vacancies/${id}/processes`)
  }

  async setVacancyProcesses(
    id: string,
    data: {
      selection_process?: Array<string | { catalog_code: string; responsible_user_id?: string }>
      hiring_process?: Array<string | { catalog_code: string; responsible_user_id?: string }>
      process_flow?: Array<{ code: string; process_type: 'selection' | 'hiring'; responsible_user_id?: string | null }>
    }
  ) {
    return this.request<{
      message: string
      selection_process: string[]
      hiring_process: string[]
    }>(`postulate/admin/vacancies/${id}/processes`, {
      method: "POST",
      body: JSON.stringify(data),
    })
  }

  async updateVacancyProcesses(
    id: string,
    data: {
      selection_process?: Array<string | { catalog_code: string; responsible_user_id?: string }>
      hiring_process?: Array<string | { catalog_code: string; responsible_user_id?: string }>
      process_flow?: Array<{ code: string; process_type: 'selection' | 'hiring'; responsible_user_id?: string | null }>
    }
  ) {
    return this.request<{
      message: string
      selection_process: string[]
      hiring_process: string[]
    }>(`postulate/admin/vacancies/${id}/processes`, {
      method: "PUT",
      body: JSON.stringify(data),
    })
  }

  async deleteVacancyProcesses(id: string) {
    return this.request<{ message: string }>(`postulate/admin/vacancies/${id}/processes`, {
      method: "DELETE",
    })
  }

  // Vacancy Templates
  async getGenericPositions() {
    return this.request<{
      generic_positions: Catalog[]
    }>("postulate/admin/vacancy-templates/generic-positions")
  }

  async getTemplateVacancy(templateId: string) {
    return this.request<{
      template: {
        id: string
        code: string | null
        name: string
        description: string | null
        metadata: Record<string, any> | null
        title: string
      }
    }>(`postulate/admin/vacancy-templates/${templateId}`)
  }

  // Templates (Reusable Templates)
  async getTemplates() {
    return this.request<{
      templates: VacancyTemplate[]
      count: number
    }>("postulate/admin/templates")
  }

  async getTemplate(id: string) {
    return this.request<{
      template: VacancyTemplate
    }>(`postulate/admin/templates/${id}`)
  }

  async createTemplate(data: CreateTemplateInput) {
    return this.request<{
      template: VacancyTemplate
    }>("postulate/admin/templates", {
      method: "POST",
      body: JSON.stringify(data),
    })
  }

  async updateTemplate(id: string, data: UpdateTemplateInput) {
    return this.request<{
      template: VacancyTemplate
    }>(`postulate/admin/templates/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    })
  }

  async deleteTemplate(id: string) {
    return this.request<{
      message: string
    }>(`postulate/admin/templates/${id}`, {
      method: "DELETE",
    })
  }

  // Process Templates
  async getProcessTemplates() {
    return this.request<{
      templates: ProcessTemplate[]
      count: number
    }>("postulate/admin/process-templates")
  }

  async getProcessTemplate(id: string) {
    return this.request<{
      template: ProcessTemplate
    }>(`postulate/admin/process-templates/${id}`)
  }

  async createProcessTemplate(data: CreateProcessTemplateInput) {
    return this.request<{
      template: ProcessTemplate
    }>("postulate/admin/process-templates", {
      method: "POST",
      body: JSON.stringify(data),
    })
  }

  async updateProcessTemplate(id: string, data: UpdateProcessTemplateInput) {
    return this.request<{
      template: ProcessTemplate
    }>(`postulate/admin/process-templates/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    })
  }

  async deleteProcessTemplate(id: string) {
    return this.request<{
      message: string
    }>(`postulate/admin/process-templates/${id}`, {
      method: "DELETE",
    })
  }

  // Applications
  async getApplications(params?: ApplicationFilters) {
    const query = this.buildQueryString(params)
    return this.request<{
      applications: Application[]
      pagination: { page: number; limit: number; total: number; total_pages: number }
    }>(`postulate/admin/applications${query}`)
  }

  async getApplication(id: string) {
    return this.request<{ application: Application; process: any[] }>(
      `postulate/admin/applications/${id}`
    )
  }

  async createApplication(data: { candidate_id: string; vacancy_id: string; [key: string]: any }) {
    return this.request<{ application: Application }>("postulate/admin/applications", {
      method: "POST",
      body: JSON.stringify(data),
    })
  }

  async updateApplication(id: string, data: UpdateApplicationInput) {
    return this.request<{ application: Application }>(`postulate/admin/applications/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    })
  }

  async deleteApplication(id: string) {
    return this.request<{ message: string }>(`postulate/admin/applications/${id}`, {
      method: "DELETE",
    })
  }

  // Application Process Management
  async getApplicationProcess(applicationId: string) {
    return this.request<any[]>(`postulate/admin/applications/${applicationId}/process`)
  }

  async updateApplicationProcessStage(applicationId: string, data: {
    stage_code: string
    process_type?: "selection" | "hiring"
    stage_name?: string
    status?: ProcessStageStatus
    notes?: string
    started_at?: string
    completed_at?: string
    candidate_names?: string | null
    current_stage_description?: string | null
    meeting_date?: string | null
    responsible_user_id?: string | null
    presentation_date?: string | null
  }) {
    return this.request<any>(`postulate/admin/applications/${applicationId}/process`, {
      method: "PUT",
      body: JSON.stringify(data),
    })
  }

  // Application History
  async getCandidateHistory(candidateId: string, filters?: CandidateHistoryFilters) {
    const queryParams = this.buildQueryString({
      ...filters,
      page: filters?.page,
      limit: filters?.limit,
      field_name: filters?.field_name,
      changed_by: filters?.changed_by,
      start_date: filters?.start_date,
      end_date: filters?.end_date,
    })
    const response = await this.request<{
      history: CandidateHistoryEntry[]
      pagination: {
        page: number
        limit: number
        total: number
        total_pages: number
      }
    }>(`postulate/admin/candidates/${candidateId}/history${queryParams}`)
    return response
  }

  async getApplicationHistory(applicationId: string) {
    return this.request<ApplicationHistoryEntry[]>(`postulate/admin/applications/${applicationId}/history`)
  }

  // Pipeline Management
  async getPipelineOverview(filters?: {
    vacancy_id?: string
    status?: string
    process_type?: "selection" | "hiring"
  }) {
    const query = this.buildQueryString(filters)
    return this.request<{
      pipeline: Array<{
        stage_key: string
        stage_name: string
        process_type: string
        count: number
        applications: Array<{
          id: string
          status: Application["status"]
          match_percentage: number | null
          created_at: string
          updated_at: string
          candidates: {
            id: string
            location: string | null
            user_profiles: {
              id: string
              email: string
              full_name: string | null
            } | null
          } | null
          vacancies: {
            id: string
            title: string
            location: string | null
            work_mode: string | null
            organizations: {
              id: string
              name: string
            } | null
          } | null
          current_stage: {
            stage_code: string | null
            stage_name: string
            process_type: string
            status: string
            stage_key: string
            days_in_stage: number
            notes: string | null
          }
          all_stages: Array<{
            id: string
            application_id: string
            stage_code: string
            process_type: string
            stage_name: string
            status: string
            notes: string | null
            started_at: string | null
            completed_at: string | null
            created_at: string
            updated_at: string
          }>
        }>
      }>
      total_applications: number
      filters: any
    }>(`postulate/admin/pipeline${query}`)
  }

  async executeStageAction(applicationId: string, data: {
    stage_code: string
    process_type?: "selection" | "hiring"
    stage_name?: string
    action_type: string
    action_data?: Record<string, any>
    notes?: string
  }) {
    return this.request<{
      stage: any
      action_type: string
      message: string
    }>(`postulate/admin/applications/${applicationId}/action`, {
      method: "POST",
      body: JSON.stringify(data),
    })
  }

  // Vacancy Pipeline Management
  async getVacanciesWithApplications(filters?: VacancyPipelineFilters) {
    const query = this.buildQueryString(filters)
    return this.request<VacancyPipelineData>(`postulate/admin/pipeline/vacancies${query}`)
  }

  async getVacancyPipeline(vacancyId: string) {
    return this.request<VacancyPipelineSection>(`postulate/admin/pipeline/vacancies/${vacancyId}`)
  }

  // Services
  async getServices() {
    return this.request<{ services: Service[] }>("postulate/admin/services")
  }

  async createService(data: CreateServiceInput) {
    return this.request<{ service: Service }>("postulate/admin/services", {
      method: "POST",
      body: JSON.stringify(data),
    })
  }

  async updateService(id: string, data: UpdateServiceInput) {
    return this.request<{ service: Service }>(`postulate/admin/services/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    })
  }

  async deleteService(id: string) {
    return this.request<{ service: Service }>(`postulate/admin/services/${id}`, {
      method: "DELETE",
    })
  }

  // Orders
  async getOrders(params?: OrderFilters) {
    const query = this.buildQueryString(params)
    return this.request<{
      orders: Order[]
      pagination: { page: number; limit: number; total: number; total_pages: number }
    }>(`postulate/admin/orders${query}`)
  }

  async getOrder(id: string) {
    return this.request<{ order: Order; items: any[] }>(`postulate/admin/orders/${id}`)
  }

  async updateOrderStatus(id: string, status: string) {
    return this.request<{ order: Order }>(`postulate/admin/orders/${id}/status`, {
      method: "PUT",
      body: JSON.stringify({ status }),
    })
  }

  // Training Courses
  async getCourses() {
    return this.request<{ courses: Course[] }>("postulate/admin/courses")
  }

  async createCourse(data: CreateCourseInput) {
    return this.request<{ course: Course }>("postulate/admin/courses", {
      method: "POST",
      body: JSON.stringify(data),
    })
  }

  async updateCourse(id: string, data: UpdateCourseInput) {
    return this.request<{ course: Course }>(`postulate/admin/courses/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    })
  }

  async deleteCourse(id: string) {
    return this.request<{ message: string }>(`postulate/admin/courses/${id}`, {
      method: "DELETE",
    })
  }

  // Notifications
  async getNotifications(params?: NotificationFilters) {
    const query = this.buildQueryString(params)
    return this.request<{
      notifications: Notification[]
      pagination: { page: number; limit: number; total: number; total_pages: number }
    }>(`postulate/admin/notifications${query}`)
  }

  async createNotification(data: CreateNotificationInput) {
    return this.request<{ notification: Notification }>("postulate/admin/notifications", {
      method: "POST",
      body: JSON.stringify(data),
    })
  }

  async updateNotification(id: string, data: { is_read?: boolean; message?: string; type?: string }) {
    return this.request<{ notification: Notification }>(`postulate/admin/notifications/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    })
  }

  async deleteNotification(id: string) {
    return this.request<{ message: string }>(`postulate/admin/notifications/${id}`, {
      method: "DELETE",
    })
  }

  // Reports & Metrics
  async getSystemMetrics() {
    return this.request<SystemMetrics>("postulate/admin/metrics")
  }

  async getRevenueReport(params: { start_date?: string; end_date?: string } = {}) {
    const query = this.buildQueryString(params)
    return this.request<RevenueReport>(`postulate/admin/reports/revenue${query}`)
  }

  async getUserActivityReport(days: number = 30) {
    return this.request<UserActivityReport>(`postulate/admin/reports/user-activity?days=${days}`)
  }

  // Catalogs
  async getCatalogs(params?: CatalogFilters) {
    const query = this.buildQueryString(params)
    return this.request<{
      catalogs: Catalog[]
    }>(`postulate/admin/catalogs${query}`)
  }

  async getCatalog(id: string) {
    return this.request<{ catalog: Catalog }>(`postulate/admin/catalogs/${id}`)
  }

  async createCatalog(data: CreateCatalogInput) {
    return this.request<{ catalog: Catalog }>("postulate/admin/catalogs", {
      method: "POST",
      body: JSON.stringify(data),
    })
  }

  async updateCatalog(id: string, data: UpdateCatalogInput) {
    return this.request<{ catalog: Catalog }>(`postulate/admin/catalogs/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    })
  }

  async deleteCatalog(id: string) {
    return this.request<{ message: string }>(`postulate/admin/catalogs/${id}`, {
      method: "DELETE",
    })
  }

  async getCatalogsByEntityType(entityType: 'candidate' | 'vacancy' | 'vacancy_template', catalogType?: string, activeOnly?: boolean) {
    const params: Record<string, any> = { entity_type: entityType }
    if (catalogType) params.catalog_type = catalogType
    if (activeOnly !== undefined) params.active_only = activeOnly
    const query = this.buildQueryString(params)
    return this.request<{
      catalogs: Catalog[]
    }>(`postulate/admin/catalogs/by-entity${query}`)
  }

  async getUniversalCatalogTypes() {
    return this.request<{
      catalog_types: Array<{
        catalog_type: string
        is_universal: boolean
        applies_to_entities: string[]
      }>
    }>(`postulate/admin/catalogs/universal-types`)
  }

  // Documents
  async uploadDocument(file: File, folder: string, filename?: string) {
    const formData = new FormData()
    formData.append("file", file)
    formData.append("folder", folder)
    if (filename) {
      formData.append("filename", filename)
    }

    const response = await fetch(`${this.baseUrl}/postulate/admin/documents/upload`, {
      method: "POST",
      body: formData,
      // Don't set Content-Type header, let browser set it with boundary
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      const errorMessage = errorData.message || errorData.body?.message || `API Error: ${response.statusText}`
      throw new Error(errorMessage)
    }

    const data = await response.json()
    
    // Backend returns data wrapped in { id, body, status, message }
    // Unwrap the body property if it exists
    if (data.body !== undefined) {
      return data.body as { key: string; filename: string; originalFilename: string; size: number; contentType: string; uploadedAt: string }
    }
    
    return data as { key: string; filename: string; originalFilename: string; size: number; contentType: string; uploadedAt: string }
  }

  getDocumentUrl(key: string, preview: boolean = false): string {
    // Return the URL to download or preview the document
    const previewParam = preview ? "&preview=true" : ""
    return `${this.baseUrl}/postulate/admin/documents/get?key=${encodeURIComponent(key)}${previewParam}`
  }

  // User Documents
  async uploadUserDocument(file: File, documentType: string, isCv?: boolean) {
    const formData = new FormData()
    formData.append("file", file)
    formData.append("document_type", documentType)
    if (isCv) {
      formData.append("is_cv", "true")
    }

    const response = await fetch(`${this.baseUrl}/users/documents/upload`, {
      method: "POST",
      body: formData,
      // Don't set Content-Type header, let browser set it with boundary
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      const errorMessage = errorData.message || errorData.body?.message || `API Error: ${response.statusText}`
      throw new Error(errorMessage)
    }

    const data = await response.json()
    
    // Backend returns data wrapped in { id, body, status, message }
    // Unwrap the body property if it exists
    if (data.body !== undefined) {
      return data.body as { key: string; filename: string; originalFilename: string; size: number; contentType: string; documentType: string; uploadedAt: string }
    }
    
    return data as { key: string; filename: string; originalFilename: string; size: number; contentType: string; documentType: string; uploadedAt: string }
  }

  async uploadCandidateDocument(candidateId: string, file: File, documentType: string, isCv?: boolean) {
    const formData = new FormData()
    formData.append("file", file)
    formData.append("document_type", documentType)
    if (isCv) {
      formData.append("is_cv", "true")
    }

    const response = await fetch(`${this.baseUrl}/postulate/admin/candidates/${candidateId}/documents/upload`, {
      method: "POST",
      body: formData,
      // Don't set Content-Type header, let browser set it with boundary
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      const errorMessage = errorData.message || errorData.body?.message || `API Error: ${response.statusText}`
      throw new Error(errorMessage)
    }

    const data = await response.json()
    
    // Backend returns data wrapped in { id, body, status, message }
    // Unwrap the body property if it exists
    if (data.body !== undefined) {
      return data.body as { key: string; filename: string; originalFilename: string; size: number; contentType: string; documentType: string; uploadedAt: string }
    }
    
    return data as { key: string; filename: string; originalFilename: string; size: number; contentType: string; documentType: string; uploadedAt: string }
  }

  async getUserDocuments() {
    return this.request<{ documents: Array<{ document_type: string; document_type_name: string; key: string; is_cv: boolean }> }>("users/documents")
  }

  getUserDocumentUrl(key: string, preview: boolean = false): string {
    // Return the URL to download or preview the document
    const previewParam = preview ? "&preview=true" : ""
    return `${this.baseUrl}/users/documents/get?key=${encodeURIComponent(key)}${previewParam}`
  }

  async deleteUserDocument(key: string) {
    return this.request<{ message: string; key: string }>(`users/documents/delete?key=${encodeURIComponent(key)}`, {
      method: "DELETE",
    })
  }

  // Matching Algorithm Configuration
  async getMatchingConfig() {
    return this.request<{
      weights: {
        hard_skills?: number | { overall: number; [skillCode: string]: number }
        soft_skills?: number | { overall: number; [skillCode: string]: number }
        software?: number | { overall: number; [skillCode: string]: number }
        skills?: number | { overall: number; [skillCode: string]: number } // Legacy support
        experience: number
        education: number
        languages: number
        areaOfInterest: number
        location: number
        salary: number
        age: number
        gender: number
      }
      message?: string
    }>("postulate/admin/matching-config")
  }

  async updateMatchingConfig(weights: {
    hard_skills?: number | { overall: number; [skillCode: string]: number }
    soft_skills?: number | { overall: number; [skillCode: string]: number }
    software?: number | { overall: number; [skillCode: string]: number }
    skills?: number | { overall: number; [skillCode: string]: number } // Legacy support
    experience: number
    education: number
    languages: number
    areaOfInterest: number
    location: number
    salary: number
    age: number
    gender: number
  }) {
    return this.request<{
      weights: typeof weights
      message?: string
    }>("postulate/admin/matching-config", {
      method: "POST",
      body: JSON.stringify({ weights }),
    })
  }

  // ============================================
  // RATING CONFIG METHODS
  // ============================================

  /**
   * Get global rating calculation configuration
   * @returns Rating configuration object
   */
  async getRatingConfig() {
    return this.request<{
      id?: string
      include_admin_ratings: boolean
      include_company_ratings: boolean
      min_rating_age_days: number | null
      max_rating_age_days: number | null
      excluded_rater_ids: string[]
      excluded_rating_ids: string[]
      created_at?: string
      updated_at?: string
      message?: string
    }>("postulate/admin/rating-config")
  }

  /**
   * Update global rating calculation configuration
   * @param config - Configuration data
   * @returns Updated rating configuration
   */
  async updateRatingConfig(config: {
    include_admin_ratings?: boolean
    include_company_ratings?: boolean
    min_rating_age_days?: number | null
    max_rating_age_days?: number | null
    excluded_rater_ids?: string[]
    excluded_rating_ids?: string[]
  }) {
    return this.request<{
      id: string
      include_admin_ratings: boolean
      include_company_ratings: boolean
      min_rating_age_days: number | null
      max_rating_age_days: number | null
      excluded_rater_ids: string[]
      excluded_rating_ids: string[]
      created_at: string
      updated_at: string
      message: string
    }>("postulate/admin/rating-config", {
      method: "POST",
      body: JSON.stringify(config),
    })
  }

  // ============================================
  // RATING STALE CONFIG METHODS
  // ============================================

  /**
   * Get all stale rating configurations
   * @returns Array of stale config objects wrapped in { configs: [...] }
   */
  async getStaleConfig() {
    return this.request<{
      configs: Array<{
        id: string
        field_type: string
        stale_threshold_months: number
        require_requalification: boolean
        created_at: string
        updated_at: string
      }>
      message?: string
    }>("postulate/admin/rating-stale-config")
  }

  /**
   * Get stale config for a specific field type
   * @param fieldType - The field type (e.g., 'hard_skills', 'languages')
   * @returns Stale config for the field type, or default if not found
   */
  async getStaleConfigForField(fieldType: string) {
    return this.request<{
      id?: string
      field_type: string
      stale_threshold_months: number
      require_requalification: boolean
      created_at?: string
      updated_at?: string
      message?: string
    }>(`postulate/admin/rating-stale-config/${encodeURIComponent(fieldType)}`)
  }

  /**
   * Update or create stale rating configuration for a field type
   * @param config - Configuration data
   * @returns Updated or created stale config
   */
  async updateStaleConfig(config: {
    id?: string
    field_type: string
    stale_threshold_months: number
    require_requalification: boolean
  }) {
    return this.request<{
      id: string
      field_type: string
      stale_threshold_months: number
      require_requalification: boolean
      created_at: string
      updated_at: string
      message: string
    }>("postulate/admin/rating-stale-config", {
      method: "POST",
      body: JSON.stringify(config),
    })
  }

  // Permissions
  async getPermissions() {
    return this.request<{
      permissions: Array<{
        id: number
        app?: string
        role: string
        scope: string
        description: string
        allowed: boolean
        created_at: string
      }>
    }>("postulate/admin/permissions")
  }

  async updatePermissions(permissions: Array<{
    id: number
    app?: string
    role: string
    scope: string
    description: string
    allowed: boolean
    created_at: string
  }>) {
    return this.request<{
      message: string
      permissions: typeof permissions
    }>("postulate/admin/permissions", {
      method: "PUT",
      body: JSON.stringify({ permissions }),
    })
  }

  // User/Candidate Profile Methods
  async getCandidateRegistration() {
    return this.request<{ registration: { complete: boolean; missing: string[]; full_name: string | null; phone: string | null } }>("users/me/registration")
  }

  async completeCandidateRegistration(full_name: string, phone: string) {
    return this.request<{ registration: { complete: boolean } }>("users/me/registration/complete", {
      method: "POST", body: JSON.stringify({ full_name, phone }),
    })
  }

  async getUserProfile() {
    return this.request<{ candidate: Candidate }>("users/me/profile")
  }

  async updateUserProfile(data: UpdateCandidateInput) {
    return this.request<{ candidate: Candidate }>("users/me/profile", {
      method: "PUT",
      body: JSON.stringify(data),
    })
  }

  async getUserCatalogs() {
    return this.request<{ catalogs: Catalog[] }>("users/me/catalogs")
  }

  // User Catalog Methods (Read-only)
  async getUserCatalogsList(params?: CatalogFilters) {
    const query = this.buildQueryString(params)
    return this.request<{
      catalogs: Catalog[]
    }>(`users/catalogs${query}`)
  }

  async getUserCatalog(id: string) {
    return this.request<{ catalog: Catalog }>(`users/catalogs/${id}`)
  }

  async getUserCatalogsByEntityType(entityType: 'candidate', catalogType?: string, activeOnly?: boolean) {
    const params: Record<string, any> = { entity_type: entityType }
    if (catalogType) params.catalog_type = catalogType
    if (activeOnly !== undefined) params.active_only = activeOnly
    const query = this.buildQueryString(params)
    return this.request<{
      catalogs: Catalog[]
    }>(`users/catalogs/by-entity${query}`)
  }

  async getUserUniversalCatalogTypes() {
    return this.request<{
      catalog_types: Array<{
        catalog_type: string
        is_universal: boolean
        applies_to_entities: string[]
      }>
    }>(`users/catalogs/universal-types`)
  }

  // ============================================
  // CANDIDATE VACANCY METHODS
  // ============================================

  async getPublicVacancies(filters?: {
    page?: number
    limit?: number
    work_mode?: string
    country?: string
    state?: string
    city?: string
    area_of_interest?: string
    search?: string
  }) {
    const query = this.buildQueryString(filters)
    return this.request<{
      vacancies: Array<Pick<Vacancy,
        | 'id'
        | 'title'
        | 'description'
        | 'salary_range'
        | 'salary_type'
        | 'country'
        | 'state'
        | 'city'
        | 'work_mode'
        | 'experience_years'
        | 'area_of_interest'
        | 'education_level'
        | 'work_schedule'
        | 'position_objective'
        | 'responsibilities'
        | 'mandatory_requirements'
        | 'benefits'
        | 'created_at'
        | 'updated_at'
      > & { organizations?: { name: string; org_type?: string | null } }>
      pagination: {
        page: number
        limit: number
        total: number
        total_pages: number
      }
    }>(`postulate/public/vacancies${query}`, {}, false, true)
  }

  async getPublicVacancy(id: string) {
    return this.request<{ vacancy: Awaited<ReturnType<ApiClient['getPublicVacancies']>>['vacancies'][number] }>(
      `postulate/public/vacancies/${encodeURIComponent(id)}`, {}, false, true,
    )
  }
  
  async getCandidateVacancies(filters?: {
    page?: number
    limit?: number
    work_mode?: string
    country?: string
    state?: string
    city?: string
    area_of_interest?: string
    search?: string
    sort_by?: 'created_at' | 'recommended'
  }) {
    const query = this.buildQueryString(filters)
    return this.request<{
      vacancies: Vacancy[]
      pagination: {
        page: number
        limit: number
        total: number
        total_pages: number
      }
    }>(`postulate/candidate/vacancies${query}`)
  }

  async getCandidateVacancy(id: string) {
    return this.request<{ vacancy: Vacancy & { has_applied?: boolean } }>(`postulate/candidate/vacancies/${id}`)
  }

  async getRecommendedVacancies(filters?: {
    page?: number
    limit?: number
    min_score?: number
  }) {
    const query = this.buildQueryString(filters)
    return this.request<{
      vacancies: (Vacancy & { recommendation_score?: number })[]
      pagination: {
        page: number
        limit: number
        total: number
        total_pages: number
      }
    }>(`postulate/candidate/vacancies/recommended${query}`)
  }

  // ============================================
  // CANDIDATE APPLICATION METHODS
  // ============================================

  async getMyApplications(filters?: {
    page?: number
    limit?: number
    status?: string
  }) {
    const query = this.buildQueryString(filters)
    return this.request<{
      applications: Application[]
      pagination: {
        page: number
        limit: number
        total: number
        total_pages: number
      }
    }>(`postulate/candidate/applications${query}`)
  }

  async getCandidateApplication(id: string) {
    return this.request<{ application: Application }>(`postulate/candidate/applications/${id}`)
  }

  async getCandidateApplicationProcess(applicationId: string) {
    return this.request<{ 
      stages: Array<{
        stage_code: string
        stage_name: string
        process_type: "selection" | "hiring"
        flow_order: number | null
        display_order: number
        status: "pending" | "in_progress" | "completed" | "skipped"
        started_at: string | null
        completed_at: string | null
        notes: string | null
      }>
    }>(`postulate/candidate/applications/${applicationId}/process`)
  }

  async applyToVacancy(vacancyId: string) {
    return this.request<{ application: Application }>("postulate/candidate/applications", {
      method: "POST",
      body: JSON.stringify({ vacancy_id: vacancyId }),
    })
  }

  // ============================================
  // USER APPS MANAGEMENT
  // ============================================

  // ============================================
  // USER APPS MANAGEMENT
  // ============================================

  /**
   * Get user app access
   * @param params - Optional filters (user_id, app)
   */
  async getUserApps(params?: { user_id?: string; app?: string }) {
    const query = this.buildQueryString(params)
    return this.request<{
      user_apps: Array<{
        id: string
        organization_id: string
        organization_name: string | null
        user_id: string
        app: string
        app_role: string
        is_active: boolean
        granted_by?: string
        granted_at: string
        expires_at?: string
        created_at: string
        updated_at: string
      }>
    }>(`general/admin/user-apps${query}`)
  }

  /**
   * Grant user access to an app
   */
  async grantUserAppAccess(data: {
    organization_id?: string
    user_id: string
    app: string
    app_role: 'admin' | 'user' | 'super_admin' | string
    is_active?: boolean
    expires_at?: string
  }) {
    return this.request<{
      user_app: {
        id: string
        user_id: string
        app: string
        app_role: string
        is_active: boolean
        granted_by?: string
        granted_at: string
        expires_at?: string
      }
    }>("general/admin/user-apps", {
      method: "POST",
      body: JSON.stringify(data),
    })
  }

  /**
   * Update user app access
   */
  async updateUserAppAccess(data: {
    user_id: string
    app: string
    app_role?: 'admin' | 'user' | 'super_admin' | string
    is_active?: boolean
    expires_at?: string
  }) {
    return this.request<{
      user_app: {
        id: string
        user_id: string
        app: string
        app_role: string
        is_active: boolean
      }
    }>("general/admin/user-apps", {
      method: "PUT",
      body: JSON.stringify(data),
    })
  }

  /**
   * Revoke user app access
   */
  async revokeUserAppAccess(userId: string, app: string) {
    return this.request<{ message: string }>(`general/admin/user-apps?user_id=${userId}&app=${app}`, {
      method: "DELETE",
    })
  }

  // ============================================
  // USER PERMISSIONS MANAGEMENT
  // ============================================

  /**
   * Get user custom permissions
   * @param params - Optional filters (user_id, app)
   */
  async getUserPermissions(params?: { user_id?: string; app?: string }) {
    const query = this.buildQueryString(params)
    return this.request<{
      user_permissions: Array<{
        id: string
        user_id: string
        app: string
        scope: string
        description?: string
        allowed: boolean
        granted_by?: string
        granted_at: string
        expires_at?: string
        created_at: string
        updated_at: string
      }>
    }>(`general/admin/user-permissions${query}`)
  }

  /**
   * Grant custom permission to user
   */
  async grantUserPermission(data: {
    user_id: string
    app: string
    scope: string
    description?: string
    allowed: boolean
    expires_at?: string
  }) {
    return this.request<{
      user_permission: {
        id: string
        user_id: string
        app: string
        scope: string
        allowed: boolean
      }
    }>("general/admin/user-permissions", {
      method: "POST",
      body: JSON.stringify(data),
    })
  }

  /**
   * Update user custom permission
   */
  async updateUserPermission(data: {
    user_id: string
    app: string
    scope: string
    allowed?: boolean
    description?: string
    expires_at?: string
  }) {
    return this.request<{
      user_permission: {
        id: string
        user_id: string
        app: string
        scope: string
        allowed: boolean
      }
    }>("general/admin/user-permissions", {
      method: "PUT",
      body: JSON.stringify(data),
    })
  }

  /**
   * Revoke user custom permission
   */
  async revokeUserPermission(userId: string, app: string, scope: string) {
    return this.request<{ message: string }>(`general/admin/user-permissions?user_id=${userId}&app=${app}&scope=${scope}`, {
      method: "DELETE",
    })
  }

  // ============================================
  // CANONICAL ORGANIZATION-APP ACCESS CONTROL
  // ============================================

  async getAccessRoleCatalog() {
    return this.request<{ roles: RoleCatalogEntry[] }>("access/roles")
  }

  async getAccessApplicationCatalog() {
    return this.request<{ applications: RoleApplication[] }>("access/applications")
  }

  async getAccessPermissionCatalog() {
    return this.request<{ permissions: RolePermission[] }>("access/permissions")
  }

  async saveAccessRole(draft: RoleDraft, existing?: Pick<RoleCatalogEntry, "id" | "updated_at">) {
    return this.request<{ role: RoleCatalogEntry }>(existing ? `access/roles/${encodeURIComponent(existing.id)}` : "access/roles", {
      method: existing ? "PUT" : "POST",
      body: JSON.stringify({ ...draft, ...(existing ? { expected_updated_at: existing.updated_at } : {}) }),
    })
  }

  private organizationHeaders(organizationId: string): HeadersInit {
    if (!organizationId) throw new Error("organizationId is required")
    return { "X-Organization-Id": organizationId }
  }

  async assignPlatformRole(userId: string, role: 'owner' | 'super_admin') {
    return this.request<{ platform_role: { user_id: string; role: 'owner' | 'super_admin' } }>("access/platform-roles", {
      method: "POST",
      body: JSON.stringify({ user_id: userId, role }),
    })
  }

  async listAccessAssignments(organizationId: string) {
    return this.request<{ assignments: AccessAssignment[] }>("access/assignments", {
      headers: this.organizationHeaders(organizationId),
    })
  }

  async assignAccess(organizationId: string, data: AccessAssignmentInput) {
    if (data.organization_id !== organizationId) throw new Error("organization scope mismatch")
    return this.request<{ assignment: AccessAssignment }>("access/assignments", {
      method: "POST",
      headers: this.organizationHeaders(organizationId),
      body: JSON.stringify(data),
    })
  }

  async syncAccess(organizationId: string, data: AccessAssignmentSyncInput) {
    if (data.organization_id !== organizationId) throw new Error("organization scope mismatch")
    return this.request<{ assignment: AccessAssignment }>("access/assignments", {
      method: "PUT",
      headers: this.organizationHeaders(organizationId),
      body: JSON.stringify(data),
    })
  }

  async revokeAccess(organizationId: string, data: Pick<AccessAssignmentInput, "organization_id" | "user_id" | "app_key">) {
    if (data.organization_id !== organizationId) throw new Error("organization scope mismatch")
    return this.request<{ assignment: AccessAssignment }>("access/assignments", {
      method: "DELETE",
      headers: this.organizationHeaders(organizationId),
      body: JSON.stringify(data),
    })
  }

  async listAccessApplications(organizationId: string) {
    return this.request<{ applications: Array<{ id: string; key: string; display_name: string; is_active: boolean }> }>("access/applications", {
      headers: this.organizationHeaders(organizationId),
    })
  }

  async listAccessRoles(organizationId: string, applicationId?: string) {
    const query = this.buildQueryString({ application_id: applicationId })
    return this.request<{ roles: Array<{ id: string; application_id: string; organization_id: string | null; key: string; display_name: string; is_system: boolean; is_delegable: boolean; is_active: boolean }> }>(`access/roles${query}`, {
      headers: this.organizationHeaders(organizationId),
    })
  }

  async listAccessPermissions(organizationId: string, applicationId?: string) {
    const query = this.buildQueryString({ application_id: applicationId })
    return this.request<{ permissions: RolePermission[] }>(`access/permissions${query}`, {
      headers: this.organizationHeaders(organizationId),
    })
  }

  // ============================================
  // ROLES MANAGEMENT
  // ============================================

  /**
   * Get all roles (system + custom)
   */
  async getRoles(params?: { app?: string; is_global?: boolean; is_system?: boolean }) {
    const query = this.buildQueryString(params)
    return this.request<{
      roles: Array<{
        id: string
        name: string
        display_name: string
        description?: string
        is_global: boolean
        app?: string
        is_system: boolean
        created_by?: string
        created_at: string
        updated_at: string
      }>
    }>(`postulate/admin/roles${query}`)
  }

  /**
   * Get role by ID
   */
  async getRoleById(roleId: string) {
    return this.request<{
      role: {
        id: string
        name: string
        display_name: string
        description?: string
        is_global: boolean
        app?: string
        is_system: boolean
        created_by?: string
        created_at: string
        updated_at: string
      }
    }>(`postulate/admin/roles/${roleId}`)
  }

  /**
   * Create custom role
   */
  async createRole(data: {
    name: string
    display_name: string
    description?: string
    is_global: boolean
    app?: string
  }) {
    return this.request<{
      role: {
        id: string
        name: string
        display_name: string
        description?: string
        is_global: boolean
        app?: string
        is_system: boolean
        created_by?: string
        created_at: string
        updated_at: string
      }
    }>(`postulate/admin/roles`, {
      method: "POST",
      body: JSON.stringify(data),
    })
  }

  /**
   * Update custom role
   */
  async updateRole(roleId: string, data: {
    display_name?: string
    description?: string
  }) {
    return this.request<{
      role: {
        id: string
        name: string
        display_name: string
        description?: string
        is_global: boolean
        app?: string
        is_system: boolean
        created_by?: string
        created_at: string
        updated_at: string
      }
    }>(`postulate/admin/roles/${roleId}`, {
      method: "PUT",
      body: JSON.stringify(data),
    })
  }

  /**
   * Delete custom role
   */
  async deleteRole(roleId: string) {
    return this.request<{ message: string }>(`postulate/admin/roles/${roleId}`, {
      method: "DELETE",
    })
  }

  /**
   * Get permissions for a role
   */
  async getRolePermissions(roleId: string) {
    return this.request<{
      permissions: Array<{
        id: number
        role: string
        app: string
        scope: string
        description?: string
        allowed: boolean
        role_type: string
        created_at: string
      }>
    }>(`postulate/admin/roles/${roleId}/permissions`)
  }

  /**
   * Update permissions for a role
   */
  async updateRolePermissions(roleId: string, permissions: Array<{
    scope: string
    app: string
    description?: string
    allowed: boolean
  }>) {
    return this.request<{
      permissions: Array<{
        id: number
        role: string
        app: string
        scope: string
        description?: string
        allowed: boolean
        role_type: string
        created_at: string
      }>
    }>(`postulate/admin/roles/${roleId}/permissions`, {
      method: "PUT",
      body: JSON.stringify({ permissions }),
    })
  }

  // ============================================
  // SYSTEM LOGS
  // ============================================

  /**
   * Get system logs with filters
   * @param params - Filter parameters (app, user_id, action_type, resource_type, date_range, status_code, success, search)
   * @returns System logs list with pagination
   */
  async getSystemLogs(params?: SystemLogFilters) {
    const query = this.buildQueryString(params)
    return this.request<{
      logs: SystemLog[]
      pagination: { page: number; limit: number; total: number; total_pages: number }
      filters: SystemLogFilters
    }>(`postulate/admin/system-logs${query}`)
  }

  // ============================================
  // QUESTIONNAIRE API
  // ============================================

  /**
   * Questionnaire Types (Super Admin Only)
   */
  async getQuestionnaireTypes() {
    return this.request<{ questionnaire_types: any[] }>(`questionnaire/types`)
  }

  async getQuestionnaireTypeById(id: string) {
    return this.request<{ questionnaire_type: any }>(`questionnaire/types/${id}`)
  }

  async createQuestionnaireType(data: { name: string; display_name: string; description?: string; config?: any; is_active?: boolean }) {
    return this.request<{ questionnaire_type: any }>(`questionnaire/types`, {
      method: "POST",
      body: JSON.stringify(data),
    })
  }

  async updateQuestionnaireType(id: string, data: { display_name?: string; description?: string; config?: any; is_active?: boolean }) {
    return this.request<{ questionnaire_type: any }>(`questionnaire/types/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    })
  }

  async deleteQuestionnaireType(id: string) {
    return this.request<{ questionnaire_type: any }>(`questionnaire/types/${id}`, {
      method: "DELETE",
    })
  }

  /**
   * Client Configs (Super Admin Only)
   */
  async getClientConfigs() {
    return this.request<{ client_configs: any[] }>(`questionnaire/client-configs`)
  }

  async getClientConfigByClientId(clientId: string) {
    return this.request<{ client_config: any }>(`questionnaire/client-configs/client/${clientId}`)
  }

  async upsertClientConfig(data: { client_id: string; max_questionnaires?: number; max_participants_per_questionnaire?: number; max_questions_per_questionnaire?: number; max_indexes_per_questionnaire?: number; max_categories_per_questionnaire?: number; tier?: string; features?: any; is_active?: boolean }) {
    return this.request<{ client_config: any }>(`questionnaire/client-configs`, {
      method: "POST",
      body: JSON.stringify(data),
    })
  }

  async updateClientConfig(clientId: string, data: { max_questionnaires?: number; max_participants_per_questionnaire?: number; max_questions_per_questionnaire?: number; max_indexes_per_questionnaire?: number; max_categories_per_questionnaire?: number; tier?: string; features?: any; is_active?: boolean }) {
    return this.request<{ client_config: any }>(`questionnaire/client-configs/client/${clientId}`, {
      method: "PUT",
      body: JSON.stringify(data),
    })
  }

  async deleteClientConfig(clientId: string) {
    return this.request<{ client_config: any }>(`questionnaire/client-configs/client/${clientId}`, {
      method: "DELETE",
    })
  }

  /**
   * User AI settings (Super Admin + own usage)
   */
  async getMyUserAiSettings(params?: { user_id?: string }) {
    const query = this.buildQueryString(params)
    return this.request<{
      settings: {
        user_id: string
        max_analyses_per_response: number
        monthly_analysis_quota: number
        monthly_analyses_used: number
        monthly_interpretation_quota: number
        monthly_interpretations_used: number
        quota_period_start: string
        notes: string | null
      }
      stats: {
        total_analyses_all_time: number
        analyses_this_month: number
      }
    }>(`questionnaire/user-ai-settings/me${query}`)
  }

  async getUserAiSettingsOverview() {
    return this.request<{ users: any[] }>(`questionnaire/user-ai-settings/overview`)
  }

  async upsertUserAiSettings(data: {
    user_id: string
    max_analyses_per_response?: number
    monthly_analysis_quota?: number
    monthly_analyses_used?: number
    notes?: string | null
    reset_monthly_usage?: boolean
  }) {
    return this.request<{ settings: any }>(`questionnaire/user-ai-settings`, {
      method: "POST",
      body: JSON.stringify(data),
    })
  }

  async getUserAiUsageHistory(params?: { user_id?: string; limit?: number; offset?: number }) {
    const query = this.buildQueryString(params)
    return this.request<{
      history: any[]
      total: number
      limit: number
      offset: number
      has_more: boolean
    }>(`questionnaire/user-ai-settings/history${query}`)
  }

  /**
   * Questionnaires
   */
  async getQuestionnaires(params?: { client_id?: string }) {
    const query = this.buildQueryString(params)
    return this.request<{ questionnaires: any[] }>(`questionnaire/admin/questionnaires${query}`)
  }

  async getQuestionnaireById(id: string) {
    return this.request<{ questionnaire: any }>(`questionnaire/admin/questionnaires/${id}`)
  }

  async createQuestionnaire(data: {
    type_id: string
    title: string
    description?: string
    status?: string
    settings?: any
    case_study_text?: string | null
    organization_id?: string | null
  }) {
    return this.request<{ questionnaire: any }>(`questionnaire/admin/questionnaires`, {
      method: "POST",
      body: JSON.stringify(data),
    })
  }

  async updateQuestionnaire(
    id: string,
    data: {
      title?: string
      description?: string
      status?: string
      settings?: any
      case_study_text?: string | null
      organization_id?: string | null
    }
  ) {
    return this.request<{ questionnaire: any }>(`questionnaire/admin/questionnaires/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    })
  }

  async createQuestionnaireFromTemplate(data: {
    template_id: string
    title: string
    organization_id?: string
    description?: string
    case_study_text?: string | null
    status?: string
    settings?: any
  }) {
    return this.request<{ questionnaire: any }>(`questionnaire/admin/questionnaires/from-template`, {
      method: "POST",
      body: JSON.stringify(data),
    })
  }

  async getQuestionnaireOrganizations() {
    return this.request<{ organizations: { id: string; name: string }[] }>(`questionnaire/admin/organizations`)
  }

  /**
   * Update indicador definition (`questionnaire_type_indexes.description`) for the questionnaire's type.
   * Questionnaire must include this index (selected_indexes) or use all indexes when none set.
   */
  async updateQuestionnaireTypeIndexDescription(
    questionnaireId: string,
    indexId: string,
    data: { description: string | null },
  ) {
    return this.request<{ index: QuestionnaireTypeIndex }>(
      `questionnaire/admin/questionnaires/${questionnaireId}/index-description/${indexId}`,
      {
        method: "PUT",
        body: JSON.stringify(data),
      },
    )
  }

  async deleteQuestionnaire(id: string) {
    return this.request<{ message: string }>(`questionnaire/admin/questionnaires/${id}`, {
      method: "DELETE",
    })
  }

  async getAssessmentTemplates(type_name?: "assessment" | "examen" | "clima_organizacional") {
    const query = type_name ? this.buildQueryString({ type_name }) : ""
    return this.request<{ templates: any[] }>(`questionnaire/assessment-templates${query}`)
  }

  async getAssessmentTemplateById(id: string, type_name?: "assessment" | "examen" | "clima_organizacional") {
    const query = type_name ? this.buildQueryString({ type_name }) : ""
    return this.request<{ template: any }>(`questionnaire/assessment-templates/${id}${query}`)
  }

  async createAssessmentTemplate(data: {
    title: string
    description?: string
    case_study_text?: string | null
    source_questionnaire_id?: string
    settings?: Record<string, unknown>
    type_name?: "assessment" | "examen" | "clima_organizacional"
  }) {
    return this.request<{ template: any }>(`questionnaire/assessment-templates`, {
      method: "POST",
      body: JSON.stringify(data),
    })
  }

  async updateAssessmentTemplate(
    id: string,
    data: { title?: string; description?: string; case_study_text?: string | null; settings?: Record<string, unknown> }
  ) {
    return this.request<{ template: any }>(`questionnaire/assessment-templates/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    })
  }

  async deleteAssessmentTemplate(id: string) {
    return this.request<{ message: string }>(`questionnaire/assessment-templates/${id}`, {
      method: "DELETE",
    })
  }

  async createAssessmentTemplateQuestion(data: {
    template_id: string
    question_text_self: string
    question_text_peer?: string
    index_id?: string
    index_ids?: string[]
    category_id?: string
    order?: number
    is_open?: boolean
    options?: any[]
    correct_option_index?: number | null
    content_by_language?: Record<string, unknown>
    ai_grading_enabled?: boolean
    ai_control_texts?: string[]
    ai_control_text_mode?: "any" | "all"
  }) {
    return this.request<{ question: any }>(`questionnaire/assessment-template-questions`, {
      method: "POST",
      body: JSON.stringify(data),
    })
  }

  async updateAssessmentTemplateQuestion(
    id: string,
    data: {
      question_text_self?: string
      question_text_peer?: string
      index_id?: string
      index_ids?: string[]
      category_id?: string
      order?: number
      is_open?: boolean
      options?: any[]
      correct_option_index?: number | null
      content_by_language?: Record<string, unknown>
      ai_grading_enabled?: boolean
      ai_control_texts?: string[]
      ai_control_text_mode?: "any" | "all"
    }
  ) {
    return this.request<{ question: any }>(`questionnaire/assessment-template-questions/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    })
  }

  async deleteAssessmentTemplateQuestion(id: string) {
    return this.request<{ message: string }>(`questionnaire/assessment-template-questions/${id}`, {
      method: "DELETE",
    })
  }

  async reorderAssessmentTemplateQuestions(data: {
    template_id: string
    question_ids: string[]
  }) {
    return this.request<{ questions: any[] }>(
      `questionnaire/assessment-template-questions/reorder`,
      {
        method: "POST",
        body: JSON.stringify(data),
      }
    )
  }

  /**
   * Questions
   */
  async getQuestions(params: { questionnaire_id: string; index_id?: string }) {
    const query = this.buildQueryString(params)
    return this.request<{ questions: any[] }>(`questionnaire/admin/questions${query}`)
  }

  async createQuestion(data: {
    questionnaire_id: string
    question_text_self: string
    question_text_peer?: string
    index_id?: string
    index_ids?: string[]
    category_id?: string
    pool_question_id?: string
    category?: string
    order?: number
    options?: any[]
    correct_option_index?: number | null
    is_open?: boolean
    ai_grading_enabled?: boolean
    ai_control_texts?: string[]
    ai_control_text_mode?: "any" | "all"
  }) {
    return this.request<{ question: any }>(`questionnaire/admin/questions`, {
      method: "POST",
      body: JSON.stringify(data),
    })
  }

  async updateQuestion(
    id: string,
    data: {
      question_text_self?: string
      question_text_peer?: string
      index_id?: string
      index_ids?: string[]
      category_id?: string
      pool_question_id?: string
      category?: string
      order?: number
      is_active?: boolean
      is_open?: boolean
      options?: any[]
      correct_option_index?: number | null
      ai_grading_enabled?: boolean
      ai_control_texts?: string[]
      ai_control_text_mode?: "any" | "all"
    }
  ) {
    return this.request<{ question: any }>(`questionnaire/admin/questions/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    })
  }

  async deleteQuestion(id: string) {
    return this.request<{ message: string }>(`questionnaire/admin/questions/${id}`, {
      method: "DELETE",
    })
  }

  async analyzeOpenQuestionResponses(data: {
    questionnaire_id: string
    question_id?: string
    evaluation_ids?: string[]
    response_ids?: string[]
    offset?: number
  }) {
    return this.request<{
      analyzed: number
      skipped_limit: number
      failed: number
      repaired?: number
      has_more: boolean
      next_offset: number | null
      batch_size?: number
      total_eligible: number
      results: Array<{
        response_id: string
        ai_score: number
        ai_explanation: string
        analysis_index: number
        ai_analysis_count: number
        skipped?: boolean
        reason?: string
      }>
    }>(`questionnaire/admin/open-questions/analyze`, {
      method: "POST",
      body: JSON.stringify(data),
    })
  }

  async suggestIndicadorInterpretation(data: {
    questionnaire_id: string
    index_name: string
    question_texts: string[]
    weight_scale_min: number
    weight_scale_max: number
    mode: "bands" | "by_weight"
    lang: "es" | "en"
  }) {
    return this.request<{
      low?: string[]
      mid?: string[]
      high?: string[]
      by_weight?: Record<string, string[]>
      monthly_interpretations_used: number
      monthly_interpretation_quota: number
    }>(`questionnaire/admin/interpretation/suggest`, {
      method: "POST",
      body: JSON.stringify(data),
    })
  }

  async translateIndicadorInterpretation(data: {
    questionnaire_id: string
    mode: "bands" | "by_weight"
    source_lang: "es" | "en"
    target_lang: "es" | "en"
    low?: string[]
    mid?: string[]
    high?: string[]
    by_weight?: Record<string, string[]>
  }) {
    return this.request<{
      low?: string[]
      mid?: string[]
      high?: string[]
      by_weight?: Record<string, string[]>
      monthly_interpretations_used: number
      monthly_interpretation_quota: number
    }>(`questionnaire/admin/interpretation/translate`, {
      method: "POST",
      body: JSON.stringify(data),
    })
  }

  async getResponseAiAnalysisHistory(response_id: string) {
    const query = this.buildQueryString({ response_id })
    return this.request<{ analyses: import("@/lib/types").QuestionnaireResponseAiAnalysis[] }>(
      `questionnaire/admin/open-questions/analysis-history${query}`
    )
  }

  async reorderQuestions(data: { questionnaire_id: string; question_ids: string[] }) {
    return this.request<{ questions: any[] }>(`questionnaire/admin/questions/reorder`, {
      method: "POST",
      body: JSON.stringify(data),
    })
  }

  async transferQuestions(data: {
    question_ids: string[]
    target_questionnaire_id: string
    mode: "copy" | "move"
    add_missing_indexes?: boolean
  }) {
    return this.request<{ copied?: number; moved?: number; questions: any[] }>(
      `questionnaire/admin/questions/transfer`,
      {
        method: "POST",
        body: JSON.stringify(data),
      }
    )
  }

  async transferAssessmentTemplateQuestions(data: {
    question_ids: string[]
    target_template_id: string
    mode: "copy" | "move"
    add_missing_indexes?: boolean
  }) {
    return this.request<{ copied?: number; moved?: number; questions: any[] }>(
      `questionnaire/assessment-template-questions/transfer`,
      {
        method: "POST",
        body: JSON.stringify(data),
      }
    )
  }

  /**
   * Participants
   */
  async getParticipants(params: { questionnaire_id: string }) {
    const query = this.buildQueryString(params)
    return this.request<{ participants: any[] }>(`questionnaire/admin/participants${query}`)
  }

  async createParticipant(data: {
    questionnaire_id: string
    name: string
    email?: string
    role?: string
    parent_participant_id?: string | null
    hierarchy_role_id?: string | null
    area_id?: string | null
    edad?: number | null
    antiguedad?: number | null
    antiguedad_unit?: "years" | "months" | null
    sexo?: "male" | "female" | "indistinto" | null
    puesto_generico?: string | null
    puesto_especifico?: string | null
    nivel_puesto?: string | null
    linea_negocio?: string | null
    pais?: string | null
    ciudad?: string | null
    lada?: string | null
    telefono?: string | null
    fecha_nacimiento?: string | null
    grado_estudios?: string | null
  }) {
    return this.request<{ participant: any }>(`questionnaire/admin/participants`, {
      method: "POST",
      body: JSON.stringify(data),
    })
  }

  async bulkCreateAssessmentParticipants(data: {
    questionnaire_id: string
    participants: AssessmentBulkParticipantRow[]
  }) {
    return this.request<AssessmentBulkParticipantsResponse>(
      `questionnaire/admin/participants/bulk`,
      {
        method: "POST",
        body: JSON.stringify(data),
      }
    )
  }

  /**
   * Splits large CSV imports into multiple bulk requests to avoid gateway/worker timeouts.
   * `onProgress` receives (processed, total) after each chunk.
   */
  async bulkCreateAssessmentParticipantsInChunks(
    data: {
      questionnaire_id: string
      participants: AssessmentBulkParticipantRow[]
    },
    options?: {
      chunkSize?: number
      onProgress?: (processed: number, total: number) => void
    }
  ): Promise<AssessmentBulkParticipantsResponse> {
    const { questionnaire_id, participants } = data
    const chunkSize = options?.chunkSize ?? 75
    const total = participants.length

    if (total === 0) {
      return { created: [], errors: [] }
    }

    const merged: AssessmentBulkParticipantsResponse = {
      created: [],
      errors: [],
    }

    for (let offset = 0; offset < total; offset += chunkSize) {
      const chunk = participants.slice(offset, offset + chunkSize)
      const res = await this.bulkCreateAssessmentParticipants({
        questionnaire_id,
        participants: chunk,
      })

      merged.created.push(...(res.created ?? []))

      for (const err of res.errors ?? []) {
        merged.errors.push({
          row: err.row + offset,
          message: err.message,
        })
      }

      options?.onProgress?.(Math.min(offset + chunk.length, total), total)
    }

    return merged
  }

  async updateParticipant(
    id: string,
    data: {
      name?: string
      email?: string
      role?: string
      parent_participant_id?: string | null
      hierarchy_role_id?: string | null
      area_id?: string | null
      edad?: number | null
      antiguedad?: number | null
      antiguedad_unit?: "years" | "months" | null
      sexo?: "male" | "female" | "indistinto" | null
      puesto_generico?: string | null
      puesto_especifico?: string | null
      nivel_puesto?: string | null
      linea_negocio?: string | null
      pais?: string | null
      ciudad?: string | null
      lada?: string | null
      telefono?: string | null
      fecha_nacimiento?: string | null
      grado_estudios?: string | null
    }
  ) {
    return this.request<{ participant: any }>(`questionnaire/admin/participants/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    })
  }

  /**
   * Update many participants of one questionnaire at once.
   * Not a transaction: `failed` lists the rows that did not apply.
   */
  async bulkUpdateParticipants(
    questionnaire_id: string,
    updates: Array<{ id: string } & Record<string, unknown>>
  ) {
    return this.request<{ updated: number; failed: { id: string; message: string }[] }>(
      `questionnaire/admin/participants/bulk`,
      {
        method: "PUT",
        body: JSON.stringify({ questionnaire_id, updates }),
      }
    )
  }

  async deleteParticipant(id: string) {
    return this.request<{ message: string }>(`questionnaire/admin/participants/${id}`, {
      method: "DELETE",
    })
  }

  /**
   * Evaluations
   */
  async getEvaluations(params: { questionnaire_id: string }) {
    if (!params.questionnaire_id) {
      throw new Error('questionnaire_id is required')
    }
    const query = this.buildQueryString(params)
    return this.request<{ evaluations: any[] }>(`questionnaire/admin/evaluations${query}`)
  }

  /**
   * Get evaluations by multiple IDs in one request (avoids rate limits).
   * @param params - evaluation_ids: array of evaluation UUIDs
   */
  async getEvaluationsBulk(params: { evaluation_ids: string[] }) {
    if (!params.evaluation_ids?.length) {
      return Promise.resolve({ evaluations: [] as any[] })
    }
    const query = `?evaluation_ids=${params.evaluation_ids.map((id) => encodeURIComponent(id)).join(',')}`
    return this.request<{ evaluations: any[] }>(`questionnaire/admin/evaluations/bulk${query}`)
  }

  async createEvaluation(data: import("./types").CreateEvaluationInput) {
    return this.request<{ evaluation: any }>(`questionnaire/admin/evaluations`, {
      method: "POST",
      body: JSON.stringify(data),
    })
  }

  async updateEvaluation(id: string, data: { evaluation_type_id?: string | null }) {
    return this.request<{ evaluation: any }>(`questionnaire/admin/evaluations/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    })
  }

  async updateEvaluationStatus(id: string, data: { access_code: string; status?: string; open_response?: string }) {
    return this.request<{ evaluation: any }>(`questionnaire/admin/evaluations/${id}/status`, {
      method: "PUT",
      body: JSON.stringify(data),
    })
  }

  /**
   * Update evaluation status via access code (public endpoint - no auth required)
   * Use this for anonymous evaluations
   */
  async updateEvaluationStatusPublic(id: string, data: { access_code: string; status?: string; open_response?: string }) {
    return this.request<{ evaluation: any }>(
      `questionnaire/user/evaluations/${id}/status`,
      {
        method: "PUT",
        body: JSON.stringify(data),
      },
      true,
      true // isPublicEndpoint = true, don't redirect on 401
    )
  }

  async deleteEvaluation(id: string) {
    return this.request<{ message: string }>(`questionnaire/admin/evaluations/${id}`, {
      method: "DELETE",
    })
  }

  /**
   * Evaluation Types (extra tag per questionnaire for display/analytics)
   */
  async getEvaluationTypes(params: { questionnaire_id: string }) {
    if (!params.questionnaire_id) throw new Error("questionnaire_id is required")
    const query = this.buildQueryString(params)
    return this.request<{ evaluation_types: import("./types").QuestionnaireEvaluationType[] }>(
      `questionnaire/admin/evaluation-types${query}`
    )
  }

  async createEvaluationType(data: { questionnaire_id: string; name: string; code?: string; is_self?: boolean }) {
    return this.request<{ evaluation_type: import("./types").QuestionnaireEvaluationType }>(
      `questionnaire/admin/evaluation-types`,
      { method: "POST", body: JSON.stringify(data) }
    )
  }

  async updateEvaluationType(id: string, data: { name?: string; code?: string; is_self?: boolean }) {
    return this.request<{ evaluation_type: import("./types").QuestionnaireEvaluationType }>(
      `questionnaire/admin/evaluation-types/${id}`,
      { method: "PUT", body: JSON.stringify(data) }
    )
  }

  async deleteEvaluationType(id: string) {
    return this.request<{ message: string }>(`questionnaire/admin/evaluation-types/${id}`, { method: "DELETE" })
  }

  /**
   * Evaluation Answers - Get and delete answers per evaluation
   */
  async getEvaluationAnswers(params: { evaluation_id: string }) {
    if (!params.evaluation_id) {
      throw new Error('evaluation_id is required')
    }
    const query = this.buildQueryString(params)
    return this.request<{ 
      responses: any[]
      unanswered_questions: any[]
      total_questions: number
      answered_count: number
      unanswered_count: number
    }>(`questionnaire/admin/evaluation-answers${query}`)
  }

  async deleteEvaluationAnswers(data: { evaluation_id: string; question_ids: string[] }) {
    if (!data.evaluation_id || !data.question_ids || !Array.isArray(data.question_ids) || data.question_ids.length === 0) {
      throw new Error('evaluation_id and question_ids (non-empty array) are required')
    }
    return this.request<{ message: string; deleted_count: number }>(`questionnaire/admin/evaluation-answers`, {
      method: "DELETE",
      body: JSON.stringify(data),
    })
  }

  /**
   * Responses
   */
  async getResponses(params: { evaluation_id: string }) {
    const query = this.buildQueryString(params)
    return this.request<{ responses: any[] }>(`questionnaire/admin/responses${query}`)
  }

  /**
   * Get one page of responses for multiple evaluations (paginated).
   * @param params - evaluation_ids, optional limit (default 2000), offset (default 0)
   */
  async getResponsesBulk(params: { evaluation_ids: string[]; limit?: number; offset?: number }) {
    if (!params.evaluation_ids?.length) {
      return Promise.resolve({ responses: [] as any[], has_more: false })
    }
    const searchParams = new URLSearchParams()
    searchParams.set('evaluation_ids', params.evaluation_ids.map((id) => encodeURIComponent(id)).join(','))
    if (params.limit != null) searchParams.set('limit', String(params.limit))
    if (params.offset != null) searchParams.set('offset', String(params.offset))
    const query = `?${searchParams.toString()}`
    return this.request<{ responses: any[]; has_more: boolean }>(`questionnaire/admin/responses/bulk${query}`)
  }

  /**
   * Fetch all responses for the given evaluations (paginated under the hood; use when there is a lot of data).
   * Chunks evaluation_ids to match server MAX_BULK so 360 analytics with many evaluations gets full data.
   */
  async getResponsesBulkAll(params: { evaluation_ids: string[] }) {
    if (!params.evaluation_ids?.length) {
      return Promise.resolve({ responses: [] as any[] })
    }
    const PAGE_SIZE = 2000
    /** Keep query string small: many UUIDs × 2000 blew past proxy URL limits and silently dropped eval ids. */
    const BULK_CHUNK = 200
    const all: any[] = []
    const ids = params.evaluation_ids
    for (let i = 0; i < ids.length; i += BULK_CHUNK) {
      const chunk = ids.slice(i, i + BULK_CHUNK)
      let offset = 0
      let has_more = true
      while (has_more) {
        const page = await this.getResponsesBulk({
          evaluation_ids: chunk,
          limit: PAGE_SIZE,
          offset,
        })
        all.push(...(page.responses || []))
        has_more = page.has_more === true
        offset += PAGE_SIZE
      }
    }
    return { responses: all }
  }

  async submitResponses(data: { access_code: string; evaluation_code: string; responses: Array<{ question_id: string; answer_value: number; answer_text?: string }>; open_response?: string; complete?: boolean }) {
    return this.request<{ responses: any[] }>(
      `questionnaire/user/responses`,
      {
        method: "POST",
        body: JSON.stringify(data),
      },
      true,
      true // isPublicEndpoint = true, don't redirect on 401
    )
  }

  /**
   * Submit partial responses for an evaluation (admin only)
   * @param data - evaluation_id, responses array, and complete flag
   * @param complete - if true, marks evaluation as completed; if false, keeps as in_progress
   */
  async submitPartialResponses(data: { evaluation_id: string; responses: Array<{ question_id: string; answer_value: number; answer_text?: string }>; complete: boolean }) {
    return this.request<{ responses: any[]; evaluation: any }>(
      `questionnaire/admin/responses`,
      {
        method: "POST",
        body: JSON.stringify(data),
      }
    )
  }

  /**
   * Access Codes (Public - No Auth Required)
   */
  async validateAccessCode(accessCode: string, lang?: string) {
    const params = new URLSearchParams({ access_code: accessCode })
    if (lang) params.set("lang", lang)
    return this.request<{ 
      participant: any; 
      questionnaire: any; 
      can_self_evaluate: boolean;
      self_evaluation_completed?: boolean;
      can_evaluate_peers: boolean;
      peer_evaluation_targets: Array<{id: string; name: string; status?: 'pending' | 'in_progress' | 'completed'}>;
      available_languages?: string[];
      content_lang?: string;
    }>(
      `questionnaire/user/access-codes/validate?${params.toString()}`,
      {},
      true,
      true // isPublicEndpoint = true, don't redirect on 401
    )
  }

  /** Public: read assessment/examen intro text by access code only (no login). */
  async getAssessmentInstructionsByAccessCode(accessCode: string, lang?: string) {
    const params = new URLSearchParams({ access_code: accessCode })
    if (lang) params.set("lang", lang)
    return this.request<{
      questionnaire_title: string
      questionnaire_type_name: string | null
      assessment_intro_text: string
      available_languages?: string[]
      content_lang?: string
      /** True for types assessment and examen (shared instruction-page UX). */
      is_assessment: boolean
    }>(
      `questionnaire/user/access-codes/instructions?${params.toString()}`,
      {},
      true,
      true
    )
  }

  async getEvaluationByAccessCode(accessCode: string, evaluationType: 'self' | 'peer', evaluateeParticipantId?: string, lang?: string) {
    const params = new URLSearchParams({
      access_code: accessCode,
      evaluation_type: evaluationType,
    });
    if (evaluateeParticipantId) {
      params.append('evaluatee_participant_id', evaluateeParticipantId);
    }
    if (lang) params.set('lang', lang);
    return this.request<{ evaluation: any; responses: any[]; answer_labels?: any; available_languages?: string[]; content_lang?: string }>(
      `questionnaire/user/access-codes/evaluations?${params.toString()}`,
      {},
      true,
      true // isPublicEndpoint = true, don't redirect on 401
    )
  }

  /**
   * Type-Level Categories (Super Admin Only)
   */
  async getTypeCategories(typeId: string) {
    return this.request<{ categories: any[] }>(`questionnaire/types/${typeId}/categories`)
  }

  async createTypeCategory(typeId: string, data: { name: string; description?: string; order?: number }) {
    return this.request<{ category: any }>(`questionnaire/types/${typeId}/categories`, {
      method: "POST",
      body: JSON.stringify(data),
    })
  }

  async updateTypeCategory(typeId: string, id: string, data: { name?: string; description?: string; order?: number; is_active?: boolean }) {
    return this.request<{ category: any }>(`questionnaire/types/${typeId}/categories/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    })
  }

  async deleteTypeCategory(typeId: string, id: string) {
    return this.request<{ category: any }>(`questionnaire/types/${typeId}/categories/${id}`, {
      method: "DELETE",
    })
  }

  /**
   * Type-Level Indexes (Super Admin Only)
   */
  async getTypeIndexes(typeId: string, params?: { type_category_id?: string }) {
    const query = this.buildQueryString(params)
    return this.request<{ indexes: any[] }>(`questionnaire/types/${typeId}/indexes${query}`)
  }

  async createTypeIndex(typeId: string, data: { type_category_id: string; name: string; description?: string; order?: number }) {
    return this.request<{ index: any }>(`questionnaire/types/${typeId}/indexes`, {
      method: "POST",
      body: JSON.stringify(data),
    })
  }

  async updateTypeIndex(typeId: string, id: string, data: { name?: string; description?: string; order?: number; is_active?: boolean }) {
    return this.request<{ index: any }>(`questionnaire/types/${typeId}/indexes/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    })
  }

  async deleteTypeIndex(typeId: string, id: string) {
    return this.request<{ index: any }>(`questionnaire/types/${typeId}/indexes/${id}`, {
      method: "DELETE",
    })
  }

  /**
   * Question Pool (Super Admin + Clients)
   */
  async getQuestionPool(typeId: string, params?: { search?: string; is_global?: string; type_index_id?: string; type_category_id?: string }) {
    const query = this.buildQueryString(params)
    return this.request<{ questions: any[] }>(`questionnaire/types/${typeId}/question-pool${query}`)
  }

  async addClimatePoolQuestions(questionnaireId: string, poolQuestionIds: string[]) {
    return this.request<{ added_count: number; skipped_count: number }>(`questionnaire/admin/questionnaires/${questionnaireId}/bank-questions`, {
      method: "POST",
      body: JSON.stringify({ pool_question_ids: poolQuestionIds }),
    })
  }

  async createQuestionPoolItem(typeId: string, data: { type_index_id?: string; type_category_id?: string; question_text: string; question_text_peer?: string; is_global?: boolean; is_open?: boolean }) {
    return this.request<{ question: any }>(`questionnaire/types/${typeId}/question-pool`, {
      method: "POST",
      body: JSON.stringify(data),
    })
  }

  async updateQuestionPoolItem(typeId: string, id: string, data: { question_text?: string; question_text_peer?: string; type_index_id?: string; type_category_id?: string; is_active?: boolean; is_open?: boolean }) {
    return this.request<{ question: any }>(`questionnaire/types/${typeId}/question-pool/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    })
  }

  async deleteQuestionPoolItem(typeId: string, id: string) {
    return this.request<{ question: any }>(`questionnaire/types/${typeId}/question-pool/${id}`, {
      method: "DELETE",
    })
  }

  /**
   * Question Pool (Client Admin - Browsing)
   */
  async browseQuestionPool(params: { type_id: string; search?: string; is_global?: boolean }) {
    const query = this.buildQueryString(params)
    return this.request<{ questions: any[] }>(`questionnaire/admin/question-pool${query}`)
  }

  /**
   * Participant Hierarchy Roles (Super Admin Only)
   */
  async getParticipantAreas() {
    return this.request<{ areas: any[] }>(`questionnaire/participant-areas`, {
      method: "GET",
    })
  }

  async createParticipantArea(data: { name: string; description?: string }) {
    return this.request<{ area: any }>(`questionnaire/participant-areas`, {
      method: "POST",
      body: JSON.stringify(data),
    })
  }

  async updateParticipantArea(id: string, data: { name?: string; description?: string; is_active?: boolean }) {
    return this.request<{ area: any }>(`questionnaire/participant-areas/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    })
  }

  async deleteParticipantArea(id: string) {
    return this.request<{ area: any }>(`questionnaire/participant-areas/${id}`, {
      method: "DELETE",
    })
  }

  async getParticipantHierarchyRoles() {
    return this.request<{ hierarchy_roles: import("./types").QuestionnaireParticipantHierarchyRole[] }>(
      `questionnaire/participant-hierarchy-roles`
    )
  }

  async createParticipantHierarchyRole(data: { name: string; description?: string; level?: number }) {
    return this.request<{ hierarchy_role: import("./types").QuestionnaireParticipantHierarchyRole }>(
      `questionnaire/participant-hierarchy-roles`,
      {
        method: "POST",
        body: JSON.stringify(data),
      }
    )
  }

  async updateParticipantHierarchyRole(id: string, data: { name?: string; description?: string; level?: number; is_active?: boolean }) {
    return this.request<{ hierarchy_role: import("./types").QuestionnaireParticipantHierarchyRole }>(
      `questionnaire/participant-hierarchy-roles/${id}`,
      {
        method: "PUT",
        body: JSON.stringify(data),
      }
    )
  }

  async deleteParticipantHierarchyRole(id: string) {
    return this.request<{ hierarchy_role: import("./types").QuestionnaireParticipantHierarchyRole }>(
      `questionnaire/participant-hierarchy-roles/${id}`,
      {
        method: "DELETE",
      }
    )
  }

  /**
   * Evaluation Relationships
   */
  async getEvaluationRelationships(params: { questionnaire_id: string }) {
    const query = this.buildQueryString(params)
    return this.request<{ relationships: import("./types").QuestionnaireEvaluationRelationship[] }>(
      `questionnaire/evaluation-relationships${query}`
    )
  }

  async createEvaluationRelationship(data: import("./types").CreateEvaluationRelationshipInput) {
    return this.request<{ relationship: import("./types").QuestionnaireEvaluationRelationship }>(
      `questionnaire/evaluation-relationships`,
      {
        method: "POST",
        body: JSON.stringify(data),
      }
    )
  }

  async deleteEvaluationRelationship(id: string) {
    return this.request<{ message: string }>(
      `questionnaire/evaluation-relationships/${id}`,
      {
        method: "DELETE",
      }
    )
  }

  async bulkUpdateEvaluationRelationships(data: import("./types").BulkUpdateEvaluationRelationshipsInput) {
    return this.request<{ message: string; added: number; removed: number; updated?: number }>(
      `questionnaire/evaluation-relationships/bulk`,
      {
        method: "POST",
        body: JSON.stringify(data),
      }
    )
  }
}

export const apiClient = new ApiClient()
